import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';

import { PortalApiService } from '../../core/portal-api.service';
import { PortalStateService } from '../../core/portal-state.service';
import { ConfirmDialogComponent } from '../../core/confirm-dialog.component';
import { I18nService } from '../../core/i18n.service';
import { formatWeekdayDate } from '../../core/months';

// Meeting food (møtemat): order from the week's planned menu for a coming day.
//
// An order placed here is a REQUEST — Frigg approves it before anything reaches
// the kitchen — and the page says so at every step, because a customer who
// thinks lunch is confirmed when it is not is the one failure mode that matters.
//
// Every rule shown here (which dates are open, a product's minimum, the
// order-value minimum) is decided by the API and merely rendered by this component.
// Nothing is re-derived in the browser, so the two cannot drift. There is no
// per-product maximum: møtemat is made to order.
@Component({
  selector: 'portal-catering',
  templateUrl: './catering.component.html',
  styleUrls: ['./catering.component.css']
})
export class CateringComponent implements OnInit, OnDestroy {

  loading = true;
  loadingMenu = false;
  submitting = false;
  error = '';
  canOrder = false;

  // From /catering/options — the server's rule, not re-derived here (see the
  // class comment). Falls back to a sane default only until the first load.
  minOrderValueIncVat = 1000;

  systems: any[] = [];
  systemDocId = '';
  dates: string[] = [];
  date = '';

  // The week's menu for the chosen date. `items` carries a `qty` the user edits
  // in place — the array itself is stable, so *ngFor is not handed new objects
  // on every change-detection pass.
  menuPlanned = false;
  menuLocked = false;
  items: any[] = [];

  desciption = '';
  comment = '';
  total = 0;
  lineCount = 0;
  belowMin: any[] = [];
  belowOrderMin = false;

  orders: any[] = [];

  private sub: Subscription;

  constructor(
    private api: PortalApiService,
    private state: PortalStateService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private i18n: I18nService
  ) { }

  ngOnInit() {
    this.sub = this.state.customerId$.subscribe(() => this.load());
  }

  ngOnDestroy() { if (this.sub) { this.sub.unsubscribe(); } }

  async load() {
    this.loading = true;
    this.error = '';
    try {
      const [options, orders]: any[] = await Promise.all([
        this.api.cateringOptions(this.state.customerId || undefined),
        this.api.cateringOrders(this.state.customerId || undefined)
      ]);

      this.canOrder = options.canOrder === true;
      this.systems = options.systems || [];
      this.dates = options.dates || [];
      this.orders = (orders && orders.orders) || [];
      if (options.minOrderValueIncVat > 0) { this.minOrderValueIncVat = options.minOrderValueIncVat; }

      if (this.systems.length && !this.systems.some(s => s.id === this.systemDocId)) {
        this.systemDocId = this.systems[0].id;
      }
      if (this.dates.length && !this.dates.includes(this.date)) {
        this.date = '';
        this.resetMenu();
      }
    } catch (err) {
      this.error = this.messageFor(err, this.i18n.t('catering.errorLoadFailed'));
    }
    this.loading = false;
  }

  private resetMenu() {
    this.menuPlanned = false;
    this.menuLocked = false;
    this.items = [];
    this.recompute();
  }

  async onDateChange() {
    if (!this.date) { this.resetMenu(); return; }

    this.loadingMenu = true;
    this.error = '';
    try {
      const res: any = await this.api.cateringOptions(this.state.customerId || undefined, this.date);
      const menu = res.menu || {};
      this.menuPlanned = menu.planned === true;
      this.menuLocked = menu.locked === true;
      // qty starts at 0; the user types over it.
      this.items = (menu.items || []).map((item: any) => ({ ...item, qty: 0 }));
      this.recompute();
    } catch (err) {
      this.error = this.messageFor(err, this.i18n.t('catering.errorMenuLoadFailed'));
      this.resetMenu();
    }
    this.loadingMenu = false;
  }

  // Normalise as it is typed: whole, non-negative. No upper clamp — there is
  // no maximum, per product or per order.
  onQtyChange(item: any) {
    let qty = Math.floor(Number(item.qty));
    if (!Number.isFinite(qty) || qty < 0) qty = 0;
    item.qty = qty;
    this.recompute();
  }

  recompute() {
    const chosen = this.items.filter(i => i.qty > 0);
    this.lineCount = chosen.length;
    this.total = chosen.reduce((sum, i) => sum + i.qty * i.price, 0);
    // Below the product's own minimum. Not auto-corrected while typing (see
    // onQtyChange) — someone building up "10" digit by digit would otherwise
    // fight a value that keeps jumping to the minimum on every keystroke.
    this.belowMin = chosen.filter(i => i.qty < i.min);
    this.belowOrderMin = this.lineCount > 0 && this.total < this.minOrderValueIncVat;
  }

  get canSubmit(): boolean {
    return this.canOrder && !this.submitting && !!this.systemDocId && !!this.date
      && this.lineCount > 0 && !!this.desciption.trim()
      && !this.belowMin.length && !this.belowOrderMin;
  }

  async submit() {
    if (!this.canSubmit) return;
    this.submitting = true;
    this.error = '';
    try {
      const res: any = await this.api.placeCateringOrder({
        systemDocId: this.systemDocId,
        customerId: this.state.customerId || undefined,
        date: this.date,
        desciption: this.desciption.trim(),
        comment: this.comment.trim(),
        lines: this.items.filter(i => i.qty > 0).map(i => ({ productType: i.productType, amount: i.qty }))
      });
      this.snack.open(
        this.i18n.t('catering.successSubmitted', { number: res.deliveryNumber }),
        'OK', { duration: 6000 });
      this.desciption = '';
      this.comment = '';
      this.date = '';
      this.resetMenu();
      await this.load();
    } catch (err) {
      this.error = this.messageFor(err, this.i18n.t('catering.errorSubmitFailed'));
    }
    this.submitting = false;
  }

  async cancel(order: any) {
    const approved = order.status === 'Sheduled';
    const ref = this.dialog.open(ConfirmDialogComponent, {
      width: '440px',
      data: {
        title: this.i18n.t('catering.confirmCancelTitle', { number: order.deliveryNumber }),
        message: this.i18n.t(approved ? 'catering.confirmCancelApproved' : 'catering.confirmCancelPending'),
        confirmLabel: this.i18n.t('catering.confirmCancelLabel')
      }
    });
    const confirmed = await ref.afterClosed().toPromise();
    if (!confirmed) return;

    try {
      await this.api.cancelCateringOrder(order.id, this.state.customerId || undefined);
      this.snack.open(this.i18n.t('catering.successCancelled'), 'OK', { duration: 4000 });
      await this.load();
    } catch (err) {
      this.snack.open(this.messageFor(err, this.i18n.t('catering.errorCancelFailed')), 'OK', { duration: 6000 });
    }
  }

  // ---- display helpers -------------------------------------------------------

  dateLabel(d: string): string {
    return d ? formatWeekdayDate(moment(d, 'YYYY-MM-DD'), this.i18n.lang) : '';
  }

  orderDate(order: any): string {
    return order.dateUnix ? formatWeekdayDate(moment.unix(order.dateUnix), this.i18n.lang) : '—';
  }

  productSummary(order: any): string {
    return (order.products || []).map((p: any) => `${p.amount} x ${p.product}`).join(', ');
  }

  statusClass(status: string): string {
    if (status === 'Sheduled') return 'pill pill-green';
    if (status === 'Pending approval') return 'pill pill-blue';
    if (status === 'Rejected') return 'pill pill-red';
    return 'pill pill-grey';
  }

  private readonly STATUS_KEYS: { [status: string]: string } = {
    'Sheduled': 'catering.statusConfirmed',
    'Pending approval': 'catering.statusPending',
    'Rejected': 'catering.statusRejected',
    'Cancelled': 'catering.statusCancelled'
  };

  statusLabel(status: string): string {
    const key = this.STATUS_KEYS[status];
    return key ? this.i18n.t(key) : this.i18n.t('catering.statusUnknown');
  }

  // `order.cancellable` is server-derived (status + the working-days cutoff
  // before delivery) — not re-checked here, matching the class comment: every
  // rule shown is decided by the API so the two cannot drift.
  cancellable(order: any): boolean {
    return this.canOrder && order.cancellable === true;
  }

  kr(v: number): string {
    return (Number(v) || 0).toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  private messageFor(err: any, fallback: string): string {
    const body = err && err.error;
    return (body && body.message) || fallback;
  }
}
